import { useState, useEffect } from "react";
import { apiV2 } from "@/api/axiosConfig";
import { useAuth } from "@/context/user";

type FeedInfo = {
    hasOnlyDefaultFeed: boolean;
    hasSamdeskFeeds: boolean;
    hasIntelligenceFusionFeeds: boolean;
    isLoading: boolean;
    defaultFeedID: string | null;
};

export const useIncidentFeeds = (clientID?: string): FeedInfo => {
    const { isSuperAdmin } = useAuth();
    const [hasOnlyDefaultFeed, setHasOnlyDefaultFeed] = useState(false);
    const [hasSamdeskFeeds, setHasSamdeskFeeds] = useState(false);
    const [hasIntelligenceFusionFeeds, setHasIntelligenceFusionFeeds] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [defaultFeedID, setDefaultFeedID] = useState<string | null>(null);

    useEffect(() => {
        const fetchFeeds = async () => {
            setIsLoading(true);
            try {
                const params: any = { limit: 1000 };

                // For super admin, use provided clientID; for others, backend uses auth context
                if (isSuperAdmin && clientID) {
                    params.clientID = clientID;
                } else if (isSuperAdmin) {
                    // Super admin hasn't selected a client yet
                    console.log("[useIncidentFeeds] Super admin without client selected");
                    setHasOnlyDefaultFeed(false);
                    setHasSamdeskFeeds(false);
                    setHasIntelligenceFusionFeeds(false);
                    setDefaultFeedID(null);
                    setIsLoading(false);
                    return;
                }

                console.log("[useIncidentFeeds] Fetching feeds with params:", params);
                const response = await apiV2.get("/incident-feeds", { params });
                const feeds = response.data.items || [];
                console.log("[useIncidentFeeds] Received feeds:", feeds);

                // Check if only one feed exists and it's the default
                const onlyDefaultFeed = feeds.length === 1 && feeds[0].isDefault === true;
                setHasOnlyDefaultFeed(onlyDefaultFeed);
                setDefaultFeedID(onlyDefaultFeed ? feeds[0].id : null);

                // Check feed types
                const hasSamdesk = feeds.some((feed: any) => feed.type === "samdesk");
                const hasIntelligenceFusion = feeds.some((feed: any) => feed.type === "intelligence_fusion");
                console.log("[useIncidentFeeds] Analysis:", { onlyDefaultFeed, hasSamdesk, hasIntelligenceFusion, feedCount: feeds.length });
                setHasSamdeskFeeds(hasSamdesk);
                setHasIntelligenceFusionFeeds(hasIntelligenceFusion);
            } catch (error) {
                console.error("Error fetching feeds:", error);
                setHasOnlyDefaultFeed(false);
                setHasSamdeskFeeds(false);
                setHasIntelligenceFusionFeeds(false);
                setDefaultFeedID(null);
            } finally {
                setIsLoading(false);
            }
        };

        fetchFeeds();
    }, [clientID, isSuperAdmin]);

    return { hasOnlyDefaultFeed, hasSamdeskFeeds, hasIntelligenceFusionFeeds, isLoading, defaultFeedID };
};
